/**
 * Guest drafts: plans drawn without an account live in this browser.
 *
 * Creating a backend plan needs an account (POST /v1/plans is auth-only),
 * but the public promise is "draw and explore freely". So a guest's plan is
 * a local draft: assignments persist to localStorage per tenant, survive a
 * reload, and are promoted to a real plan the moment the guest signs in
 * (see `promoteLocalDraft` in the painting store).
 *
 * Every storage access is wrapped: private windows and blocked storage throw,
 * and the editor must keep working (in memory) when they do.
 */
import type { PlanSummary } from './plans';

export interface StoredDraft {
  id: string;
  name: string;
  district_count: number;
  updated_at: string;
  assignments: Record<string, number>;
  /** The author's comments on the plan. */
  comments?: string;
  /** Custom district names ("" = default). */
  district_labels?: string[];
}

const PREFIX = 'rd:guest-drafts:';

function key(tenantSlug: string): string {
  return `${PREFIX}${tenantSlug}`;
}

function readAll(tenantSlug: string): StoredDraft[] {
  try {
    const raw = window.localStorage.getItem(key(tenantSlug));
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (d): d is StoredDraft =>
        typeof d === 'object' &&
        d !== null &&
        typeof (d as StoredDraft).id === 'string' &&
        typeof (d as StoredDraft).name === 'string' &&
        typeof (d as StoredDraft).assignments === 'object',
    );
  } catch {
    return [];
  }
}

function writeAll(tenantSlug: string, drafts: StoredDraft[]): boolean {
  try {
    window.localStorage.setItem(key(tenantSlug), JSON.stringify(drafts));
    return true;
  } catch {
    return false;
  }
}

export function listLocalDrafts(tenantSlug: string): PlanSummary[] {
  return readAll(tenantSlug).map((d) => ({
    id: d.id,
    name: d.name,
    district_count: d.district_count,
    status: 'draft' as const,
    updated_at: d.updated_at,
  }));
}

export function readLocalDraft(tenantSlug: string, id: string): StoredDraft | null {
  return readAll(tenantSlug).find((d) => d.id === id) ?? null;
}

/** Upsert a draft. Returns false when storage is unavailable (kept in memory only). */
export function writeLocalDraft(tenantSlug: string, draft: StoredDraft): boolean {
  const others = readAll(tenantSlug).filter((d) => d.id !== draft.id);
  return writeAll(tenantSlug, [...others, draft]);
}

export function deleteLocalDraft(tenantSlug: string, id: string): void {
  writeAll(
    tenantSlug,
    readAll(tenantSlug).filter((d) => d.id !== id),
  );
}

export function isLocalPlanId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith('local-');
}
